import * as THREE from 'three';
import { SHADOW } from '../core/SimConfig.js';

const _forward = new THREE.Vector3();

/**
 * Cosmetic ground AO shadow rendered as a single horizontal plane beneath
 * the aircraft. Uses shadow.png as an alpha map on a black basic material;
 * orientation tracks the aircraft heading only (ignores roll/pitch and
 * terrain slope). Fades out with altitude.
 */
export default class AircraftShadow {
  constructor({ scene, texture }) {
    this.scene = scene;

    const geometry = new THREE.PlaneGeometry(SHADOW.size, SHADOW.size);
    // PlaneGeometry lies in XY (normal +Z). Rotate to lie flat in XZ (normal +Y).
    geometry.rotateX(-Math.PI / 2);

    this.material = new THREE.MeshBasicMaterial({
      color: 0x000000,
      alphaMap: texture,
      transparent: true,
      opacity: SHADOW.strength,
      depthWrite: false,
      // Render after opaque terrain so the alpha-blended shadow composits correctly.
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  dispose() {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  update(aircraft, terrainCollider) {
    if (!aircraft || !terrainCollider) {
      this.mesh.visible = false;
      return;
    }

    const pos = aircraft.position;
    const groundY = terrainCollider.getHeightAt(pos.x, pos.z);
    if (groundY == null) {
      this.mesh.visible = false;
      return;
    }

    // Altitude fade — full strength below fadeStartAGL, gone past fadeEndAGL.
    const agl = pos.y - groundY;
    const fadeRange = SHADOW.fadeEndAGL - SHADOW.fadeStartAGL;
    let altFade = 1;
    if (agl >= SHADOW.fadeEndAGL) {
      this.mesh.visible = false;
      return;
    } else if (agl > SHADOW.fadeStartAGL && fadeRange > 0) {
      altFade = 1 - (agl - SHADOW.fadeStartAGL) / fadeRange;
    }

    // Heading from aircraft forward (body -Z) projected onto world XZ plane.
    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    const heading = Math.atan2(-_forward.x, -_forward.z);

    this.mesh.position.set(pos.x, groundY + SHADOW.yOffset, pos.z);
    this.mesh.rotation.y = heading;
    this.material.opacity = SHADOW.strength * altFade;
    this.mesh.visible = true;
  }
}
